import { TEMPLATE_SET_KEY_REGEX } from '@pams/shared';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [P7B §3c] "CREATE A POLICY COVERING THESE" — THE NEW-POLICY FORM, PREFILLED FROM THE URL.
 *
 *  `/policies/new?postTypeIds=a,b&vehicleTypeIds=c&name=…&from=templates&setKey=…` pre-selects those
 *  columns and the name. The policy author then reviews, saves and publishes through the ordinary flow
 *  — `assertMayAuthorPolicy`, `POLICY_CREATED` and `POLICY_VERSION_PUBLISHED` unchanged — and comes back
 *  to the template decisions to choose it. No template code creates a policy.
 *
 *  ⚠️ `null` WHEN NONE OF THE THREE IS PRESENT, so `/policies/new` with no parameters is byte-for-byte
 *  what it was. An id that is not an ObjectId is dropped rather than sent: a hand-edited link must not
 *  put a value in the form that the create would refuse with a 422 naming a column nobody chose.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
export type PolicyPrefill = {
  name: string | null;
  postTypes: string[];
  vehicleTypes: string[];
  /** The template set to return to, when the link came from the template decisions. */
  templatesSetKey: string | null;
};

const OBJECT_ID = /^[a-f0-9]{24}$/i;

const idsOf = (value: string | null): string[] =>
  value === null ? [] : [...new Set(value.split(',').filter((id) => OBJECT_ID.test(id.trim())))];

export const policyPrefillOf = (params: URLSearchParams): PolicyPrefill | null => {
  if (!params.has('postTypeIds') && !params.has('vehicleTypeIds') && !params.has('name')) return null;
  const name = params.get('name');
  const setKey = params.get('setKey');
  return {
    name: name === null || name.trim() === '' ? null : name.trim().slice(0, 160),
    postTypes: idsOf(params.get('postTypeIds')),
    vehicleTypes: idsOf(params.get('vehicleTypeIds')),
    templatesSetKey:
      params.get('from') === 'templates' && setKey !== null && TEMPLATE_SET_KEY_REGEX.test(setKey) ? setKey : null,
  };
};
